import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { catchError, map, of, switchMap } from 'rxjs';

import { TranslationModule } from '@core/translation/translation.module';
import { OrdersApi } from '@bcmServices/index';
import { BcmOrder } from '@shared/models/bcm-order';

import { BookingV2Facade } from '../../services/booking-v2.facade';
import {
    filterOrdersForBookingClient,
    formatOrderOptionLabel,
    pickDefaultOrderIdForBooking,
} from '../../utils/booking-v2-order-link.util';

interface OrderLinkLoadKey {
    personId: number | null;
    companyId: number | null;
    boatId: number | null;
    autoPickKey: string;
}

@Component({
    selector: 'booking-v2-order-link-card',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, TranslationModule, Select],
    templateUrl: './booking-v2-order-link-card.component.html',
    host: { class: 'block' },
})
export class BookingV2OrderLinkCardComponent {
    private readonly facade = inject(BookingV2Facade);
    private readonly ordersApi = inject(OrdersApi);

    readonly selectedPerson = this.facade.selectedPerson;
    readonly selectedBoat = this.facade.selectedBoat;
    readonly selectedOrderId = this.facade.selectedOrderId;

    private readonly loadKey = computed<OrderLinkLoadKey | null>(() => {
        const person = this.selectedPerson();

        if (!person?.id) {
            return null;
        }

        const personId = person.kind === 'person' ? person.id : null;
        const companyId = person.kind === 'company' ? person.id : null;

        return {
            personId,
            companyId,
            boatId: this.selectedBoat()?.id ?? null,
            autoPickKey: `${personId ?? ''}:${companyId ?? ''}`,
        };
    });

    readonly openOrders = toSignal(
        toObservable(this.loadKey).pipe(
            switchMap((key) => {
                if (!key) {
                    return of([] as readonly BcmOrder[]);
                }

                return this.ordersApi.getMany().pipe(
                    map((orders) => filterOrdersForBookingClient(orders, key.personId, key.companyId)),
                    catchError(() => of([] as readonly BcmOrder[])),
                );
            }),
        ),
        { initialValue: [] as readonly BcmOrder[] },
    );

    readonly orderOptions = computed(() =>
        (this.openOrders() ?? []).map((order) => ({
            label: formatOrderOptionLabel(order),
            value: order.id,
        })),
    );

    readonly showCard = computed(() => !!this.selectedPerson());

    constructor() {
        // Auto-pick once per guest; writes stay in untracked() so we never
        // retrigger this effect via selectedOrderId (avoids CD / HTTP loops).
        effect(() => {
            const key = this.loadKey();
            const orders = this.openOrders() ?? [];

            untracked(() => {
                if (!key) {
                    if (this.facade.selectedOrderId() != null) {
                        this.facade.selectedOrderId.set(null);
                    }
                    return;
                }

                const current = this.facade.selectedOrderId();

                if (current != null && !orders.some((order) => order.id === current)) {
                    this.facade.selectedOrderId.set(null);
                    return;
                }

                if (current == null && orders.length) {
                    const defaultId = pickDefaultOrderIdForBooking(orders, key.boatId);
                    if (defaultId != null) {
                        this.facade.selectedOrderId.set(defaultId);
                    }
                }
            });
        });
    }

    onOrderChange(orderId: number | null): void {
        this.facade.selectedOrderId.set(orderId && orderId > 0 ? orderId : null);
    }
}
